import React, { useEffect, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import courseService from '../services/course.service';
import VideoPlayer from '../components/VideoPlayer';

const UnitDetailPage = () => {
  const { unitSlug } = useParams();
  const location = useLocation();
  const initialUnitId = location.state?.unitId || courseService.resolveUnitIdBySlug(unitSlug) || null;
  const [resolvedUnitId, setResolvedUnitId] = useState(initialUnitId);
  const [resolvingUnitId, setResolvingUnitId] = useState(!initialUnitId && Boolean(unitSlug));
  const [unit, setUnit] = useState(() => (initialUnitId ? courseService.getCachedUnit(initialUnitId) : null));
  const [loading, setLoading] = useState(!initialUnitId);
  const [error, setError] = useState(null);
  const [descExpanded, setDescExpanded] = useState(false);

  useEffect(() => {
    if (!unitSlug) {
      return;
    }

    if (location.state?.unitId) {
      setResolvedUnitId(location.state.unitId);
      setResolvingUnitId(false);
      return;
    }

    const cachedId = courseService.resolveUnitIdBySlug(unitSlug);
    if (cachedId) {
      setResolvedUnitId(cachedId);
      setResolvingUnitId(false);
      return;
    }

    let active = true;

    const resolveUnit = async () => {
      try {
        setResolvingUnitId(true);
        setLoading(true);
        await courseService.getCourses({ include_sections: 1, include_subject_groups: 1 });
        if (!active) {
          return;
        }
        const matchedId = courseService.resolveUnitIdBySlug(unitSlug);
        if (matchedId) {
          setResolvedUnitId(matchedId);
          setError(null);
        } else {
          setError('Unit not found.');
          setUnit(null);
          setLoading(false);
        }
      } catch (err) {
        if (!active) {
          return;
        }
        console.error('Failed to resolve unit slug', err);
        setError('Unable to resolve unit right now. Please try again later.');
        setUnit(null);
        setLoading(false);
      } finally {
        if (active) {
          setResolvingUnitId(false);
        }
      }
    };

    resolveUnit();

    return () => {
      active = false;
    };
  }, [unitSlug, location.state]);

  useEffect(() => {
    const fetchUnit = async () => {
      if (!resolvedUnitId) {
        return;
      }

      const cachedUnit = courseService.getCachedUnit(resolvedUnitId);
      if (cachedUnit) {
        setUnit(cachedUnit);
        setError(null);
        setLoading(false);
        return;
      }

      setLoading(true);
      try {
        const detail = await courseService.getUnit(resolvedUnitId);
        if (!detail) {
          setError('Unit not found.');
          setUnit(null);
        } else {
          setUnit(detail);
        }
      } catch (err) {
        setError('Failed to load unit.');
        setUnit(null);
      } finally {
        setLoading(false);
      }
    };

    if (resolvedUnitId) {
      fetchUnit();
    } else if (!resolvingUnitId) {
      setError('Invalid unit identifier.');
      setLoading(false);
    }
  }, [resolvedUnitId, resolvingUnitId]);

  if (loading) return <div className="py-16 text-center text-text-secondary">Loading...</div>;
  if (error) return <div className="py-16 text-center text-red-500">{error}</div>;
  if (!unit) return null;

  return (
    <div className="min-h-screen bg-bg-default">
      <div className="page-container py-8 flex flex-col items-center">
        <div className="w-full flex flex-col items-center">
          <div className="w-full flex justify-center mb-2 md:mb-4">
            <div className="w-full max-w-3xl md:min-h-[400px]">
              <VideoPlayer videoUrl={unit.video_url} unitId={unit.id} />
              <h1 className="text-2xl md:text-3xl font-extrabold text-primary leading-tight mb-1 mt-2 md:mt-3 text-left">{unit.title}</h1>
              {unit.taught_by_details && (
                <div className="text-base md:text-lg font-medium text-gray-700 text-left mb-0">
                  <span className="text-gray-500">By:</span> <span className="text-primary font-bold">{unit.taught_by_details.full_name}</span>
                </div>
              )}
            </div>
          </div>
    </div>
    <div className="w-full max-w-3xl flex flex-col gap-2 md:gap-4">
          {unit.description && (
            <div className="card p-4 text-sm md:text-base text-gray-700 whitespace-pre-line w-full">
              {descExpanded
                ? unit.description
                : unit.description.split('\n').slice(0, 2).join('\n').slice(0, 220) + (unit.description.length > 220 ? '...' : '')}
              {unit.description.length > 220 && !descExpanded && (
                <button
                  className="ml-2 text-primary underline text-xs"
                  onClick={() => setDescExpanded(true)}
                >
                  Read more
                </button>
              )}
              {descExpanded && (
                <button
                  className="ml-2 text-primary underline text-xs"
                  onClick={() => setDescExpanded(false)}
                >
                  Show less
                </button>
              )}
            </div>
          )}
          {unit.files && unit.files.length > 0 && (
            <div className="card p-4 w-full">
              <h2 className="text-lg font-semibold mb-2">Files</h2>
              <ul className="space-y-2">
                {unit.files.map(file => (
                  <li key={file.id}>
                    <a href={file.file} target="_blank" rel="noopener noreferrer" className="text-primary underline">
                      {file.name || 'Download'}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default UnitDetailPage;
